import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ChevronDown, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { useState } from "react";

import { PAYROLL_VOUCHER_LABELS } from "../messages";

const MONTH_LABELS: readonly string[] = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const MIN_YEAR: number = 2020;

const TOOLTIP_DELAY_MS: number = 100;

const TOOLTIP_SIDE_OFFSET: number = 5;

const MENU_ITEM_CLASS_NAME: string =
  "w-full rounded px-2 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-transparent";

export interface ExportMenuItem {
  key: string;
  label: string;
  disabled?: boolean;
  disabledReason?: string;
  onSelect: () => void;
}

export interface ConsolidatedExportItem {
  key: string;
  label: string;
  onSelect: (year: number, month: number) => void;
}

interface ExportTablesMenuProps {
  items: readonly ExportMenuItem[];
  consolidatedItems: readonly ConsolidatedExportItem[];
  currentCycle: string;
}

interface FiscalCyclePickerProps {
  currentCycle: string;
  onSelect: (year: number, month: number) => void;
}

const cycleLabelOf = (year: number, monthIndex: number): string =>
  `${year}-${String(monthIndex + 1).padStart(2, "0")}`;

function FiscalCyclePicker({
  currentCycle,
  onSelect,
}: FiscalCyclePickerProps): JSX.Element {
  const maxYear: number = new Date().getFullYear();
  const [year, setYear] = useState<number>(maxYear);

  // O ciclo EM CURSO é exportável (ADR-PAY-006 da API); só o que ainda não começou
  // fica de fora. Quem define o ciclo corrente é a API — aqui só se compara o
  // rótulo `AAAA-MM`, que é lexicograficamente ordenável. Regra de ciclo não
  // mora no Front (regra 5).
  const isAfterCurrentCycle = (monthIndex: number): boolean =>
    cycleLabelOf(year, monthIndex) > currentCycle;

  return (
    <div className="mt-2 rounded-md border p-2">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost_none"
          className="h-6 w-6 border-none bg-transparent p-0"
          aria-label="Ano anterior"
          disabled={year <= MIN_YEAR}
          onClick={() => setYear((current: number) => current - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium">{year}</span>
        <Button
          variant="ghost_none"
          className="h-6 w-6 border-none bg-transparent p-0"
          aria-label="Próximo ano"
          disabled={year >= maxYear}
          onClick={() => setYear((current: number) => current + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1">
        {MONTH_LABELS.map((month: string, index: number) => (
          <button
            key={month}
            type="button"
            disabled={isAfterCurrentCycle(index)}
            onClick={() => onSelect(year, index + 1)}
            className="rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-transparent"
          >
            {month}
          </button>
        ))}
      </div>
    </div>
  );
}

interface ExportMenuButtonProps {
  item: ExportMenuItem;
}

function ExportMenuButton({ item }: ExportMenuButtonProps): JSX.Element {
  const button: JSX.Element = (
    <button
      type="button"
      disabled={item.disabled}
      onClick={item.onSelect}
      className={MENU_ITEM_CLASS_NAME}
    >
      {item.label}
    </button>
  );

  if (!item.disabled || !item.disabledReason) return button;

  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block w-full">{button}</span>
        </TooltipTrigger>
        <TooltipContent side="left" sideOffset={TOOLTIP_SIDE_OFFSET}>
          <p className="text-sm">{item.disabledReason}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ExportTablesMenu({
  items,
  consolidatedItems,
  currentCycle,
}: ExportTablesMenuProps): JSX.Element {
  const [openKey, setOpenKey] = useState<string | null>(null);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          className="h-9 bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90"
        >
          <Download className="mr-2 h-4 w-4" />
          {PAYROLL_VOUCHER_LABELS.EXPORT_MENU}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[264px] p-2">
        {items.map((item: ExportMenuItem) => (
          <ExportMenuButton key={item.key} item={item} />
        ))}

        {consolidatedItems.length > 0 && <hr className="my-2" />}

        {consolidatedItems.map((item: ConsolidatedExportItem) => (
          <div key={item.key}>
            <button
              type="button"
              onClick={() =>
                setOpenKey((current: string | null) =>
                  current === item.key ? null : item.key,
                )
              }
              className="flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
            >
              {item.label}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  openKey === item.key ? "rotate-180" : ""
                }`}
              />
            </button>
            {openKey === item.key && (
              <FiscalCyclePicker
                currentCycle={currentCycle}
                onSelect={item.onSelect}
              />
            )}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
